import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Logo } from "@/components/site/Logo";
import { getErrorMessage } from "@/lib/errors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "monia_admin_token";

const AnswerDialog = ({ open, onClose, request, token, onAnswered }) => {
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (request) {
      const initial = (request.questions || []).map((_, i) => request.answers?.[i] || "");
      setAnswers(initial);
    }
  }, [request]);

  if (!request) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!answers.some((a) => (a || "").trim())) {
      toast.error("Vul minstens één antwoord in");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/requests/${request.id}/answer`,
        { answers },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (data.email_sent) toast.success("Antwoord verzonden per e-mail");
      else toast.success("Antwoord opgeslagen (e-mail verzending mislukt)");
      onAnswered();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, "Verzenden mislukt"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-espresso border-gold/30 text-sand max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="answer-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-sand">
            Antwoord aan {request.name}
          </DialogTitle>
          <DialogDescription className="text-sand/60">
            {request.email} · {request.lang?.toUpperCase()}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="mt-2 space-y-5">
          {request.context && (
            <div className="rounded-lg border border-gold/25 bg-ivy/40 p-3" data-testid="dialog-context-block">
              <div className="text-gold/80 text-[10px] uppercase tracking-widest mb-1">Context van klant</div>
              <p className="text-sand/85 text-sm whitespace-pre-wrap leading-relaxed">{request.context}</p>
            </div>
          )}
          {(request.questions || []).map((q, i) => (
            <div key={i}>
              <div className="text-gold/80 text-xs uppercase tracking-widest mb-1">Vraag {i + 1}</div>
              <p className="text-sand/80 italic text-sm mb-2">{q}</p>
              <Textarea
                value={answers[i] || ""}
                onChange={(e) => setAnswers((a) => a.map((v, idx) => (idx === i ? e.target.value : v)))}
                rows={5}
                placeholder="Schrijf hier je antwoord..."
                className="bg-ivy/60 border-gold/25 text-sand focus-visible:ring-gold"
                data-testid={`answer-textarea-${i}`}
              />
            </div>
          ))}
          <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-espresso pb-1">
            <Button type="button" variant="ghost" onClick={onClose} className="text-sand/70 hover:text-gold hover:!bg-gold/10" data-testid="answer-cancel-button">
              Annuleren
            </Button>
            <Button type="submit" disabled={loading} className="rounded-full bg-rust hover:bg-rust/90 text-cream px-6 h-10" data-testid="answer-send-button">
              {loading ? "Verzenden..." : "Verzenden per e-mail"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const LoginView = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token, data.must_change_password);
    } catch (err) {
      toast.error(getErrorMessage(err, "Onjuiste e-mail of wachtwoord"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen mystic-bg grain flex items-center justify-center px-5">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-gold/25 bg-espresso/50 p-8 backdrop-blur-sm" data-testid="admin-login-form">
        <div className="flex items-center gap-3 mb-6">
          <Logo className="text-gold" size={36} />
          <span className="font-serifluxe text-sand tracking-[0.2em] uppercase">Ask Monia · Admin</span>
        </div>
        <label className="text-sand/70 text-xs uppercase tracking-widest">E-mail</label>
        <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 mb-4 h-11 bg-ivy/60 border-gold/25 text-sand" data-testid="admin-email-input" />
        <label className="text-sand/70 text-xs uppercase tracking-widest">Wachtwoord</label>
        <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 mb-6 h-11 bg-ivy/60 border-gold/25 text-sand" data-testid="admin-password-input" />
        <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-rust hover:bg-rust/90 text-cream" data-testid="admin-login-button">
          {loading ? "..." : "Inloggen"}
        </Button>
      </form>
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div className="rounded-xl border border-gold/20 bg-espresso/40 p-5">
    <div className="font-display text-gold text-3xl">{value}</div>
    <div className="text-sand/60 text-xs uppercase tracking-widest mt-1">{label}</div>
  </div>
);

const ChangePasswordPanel = ({ token, mustChange, onDone }) => {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nw !== confirm) { toast.error("De nieuwe wachtwoorden komen niet overeen"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/change-password`, { current_password: cur, new_password: nw },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Wachtwoord gewijzigd");
      setCur(""); setNw(""); setConfirm("");
      onDone();
    } catch (err) {
      toast.error(getErrorMessage(err, "Wijzigen mislukt"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="mb-10 rounded-xl border border-gold/25 bg-espresso/50 p-6" data-testid="change-password-panel">
      <h3 className="font-display text-sand text-xl mb-1">Wachtwoord wijzigen</h3>
      {mustChange && <p className="text-gold/80 text-sm mb-4">Kies nu je eigen wachtwoord (het tijdelijke wachtwoord vervangen).</p>}
      <div className="grid sm:grid-cols-3 gap-3">
        <Input type="password" required placeholder="Huidig wachtwoord" value={cur} onChange={(e) => setCur(e.target.value)} className="h-11 bg-ivy/60 border-gold/25 text-sand" data-testid="cur-password-input" />
        <Input type="password" required placeholder="Nieuw wachtwoord" value={nw} onChange={(e) => setNw(e.target.value)} className="h-11 bg-ivy/60 border-gold/25 text-sand" data-testid="new-password-input" />
        <Input type="password" required placeholder="Herhaal nieuw wachtwoord" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11 bg-ivy/60 border-gold/25 text-sand" data-testid="confirm-password-input" />
      </div>
      <Button type="submit" disabled={loading} className="mt-4 rounded-full bg-rust hover:bg-rust/90 text-cream px-6 h-10" data-testid="save-password-button">
        {loading ? "..." : "Opslaan"}
      </Button>
    </form>
  );
};

const Dashboard = ({ token, onLogout, mustChange }) => {
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [pwOpen, setPwOpen] = useState(!!mustChange);
  const [answerReq, setAnswerReq] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const [s, su, rq] = await Promise.all([
        axios.get(`${API}/admin/stats`, auth),
        axios.get(`${API}/admin/subscriptions`, auth),
        axios.get(`${API}/admin/requests`, auth),
      ]);
      setStats(s.data);
      setSubs(su.data);
      setRequests(rq.data);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
      else toast.error("Laden mislukt");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("nl-NL") : "—");
  const badge = (status) => {
    const map = { paid: "bg-moss/30 text-moss", covered: "bg-gold/20 text-gold", pending: "bg-burgundy/30 text-sand/80" };
    return `inline-block rounded-full px-2.5 py-0.5 text-xs ${map[status] || "bg-sand/10 text-sand/70"}`;
  };

  return (
    <div className="min-h-screen mystic-bg grain">
      <div className="max-w-6xl mx-auto px-5 py-10">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Logo className="text-gold" size={34} />
            <span className="font-serifluxe text-sand tracking-[0.2em] uppercase">Ask Monia · Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setPwOpen((v) => !v)} variant="ghost" className="text-sand/80 hover:text-gold hover:!bg-gold/10" data-testid="toggle-change-password">Wachtwoord wijzigen</Button>
            <Button onClick={onLogout} variant="ghost" className="text-sand/80 hover:text-gold hover:!bg-gold/10" data-testid="admin-logout-button">Uitloggen</Button>
          </div>
        </div>

        {pwOpen && <ChangePasswordPanel token={token} mustChange={mustChange} onDone={() => setPwOpen(false)} />}

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
            <StatCard label="Aanvragen" value={stats.total_requests} />
            <StatCard label="Betaald/gedekt" value={stats.paid} />
            <StatCard label="Actieve abonnementen" value={stats.active_subscriptions} />
            <StatCard label="Gids-aanvragen" value={stats.guide_leads} />
          </div>
        )}

        {/* Subscriptions */}
        <h2 className="font-display text-sand text-2xl mb-4">Jaarabonnementen</h2>
        <div className="overflow-x-auto rounded-xl border border-gold/20 mb-12" data-testid="admin-subscriptions-table">
          <table className="w-full text-sm">
            <thead className="bg-espresso/60 text-gold/80 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Naam</th><th className="text-left p-3">E-mail</th>
                <th className="text-left p-3">Gebruikt</th><th className="text-left p-3">Over</th>
                <th className="text-left p-3">Status</th><th className="text-left p-3">Verloopt</th>
              </tr>
            </thead>
            <tbody>
              {subs.length === 0 && <tr><td colSpan={6} className="p-4 text-sand/50 text-center">Nog geen abonnementen</td></tr>}
              {subs.map((s) => (
                <tr key={s.id} className="border-t border-gold/10 text-sand/80">
                  <td className="p-3">{s.name}</td>
                  <td className="p-3">{s.email}</td>
                  <td className="p-3">{s.questions_used} / {s.questions_total}</td>
                  <td className="p-3 text-gold font-medium">{s.remaining}</td>
                  <td className="p-3">{s.status}</td>
                  <td className="p-3">{fmtDate(s.end_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Requests */}
        <h2 className="font-display text-sand text-2xl mb-4">Alle aanvragen</h2>
        <div className="space-y-3" data-testid="admin-requests-list">
          {requests.length === 0 && <p className="text-sand/50">Nog geen aanvragen</p>}
          {requests.map((r) => (
            <div key={r.id} className="rounded-xl border border-gold/15 bg-espresso/40 p-4" data-testid={`admin-request-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="text-sand font-medium">{r.name}</span>
                  <span className="text-sand/50"> · {r.email}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sand/60 text-xs uppercase">{r.package}</span>
                  <span className="font-display text-gold">€ {Number(r.amount).toFixed(2)}</span>
                  <span className={badge(r.status)}>{r.status}</span>
                  {r.answered && (
                    <span className="inline-block rounded-full px-2.5 py-0.5 text-xs bg-moss/30 text-moss" data-testid={`answered-badge-${r.id}`}>
                      beantwoord
                    </span>
                  )}
                  <span className="text-sand/50 text-xs">{fmtDate(r.created_at)}</span>
                </div>
              </div>
              {r.context && (
                <div className="mt-3 rounded-lg border border-gold/15 bg-ivy/30 p-3" data-testid={`context-block-${r.id}`}>
                  <div className="text-gold/70 text-[10px] uppercase tracking-widest mb-1">Context van klant</div>
                  <p className="text-sand/80 text-sm whitespace-pre-wrap leading-relaxed">{r.context}</p>
                </div>
              )}
              <ul className="mt-3 space-y-1">
                {(r.questions || []).map((q, i) => (
                  <li key={i} className="text-sand/70 text-sm flex gap-2"><span className="text-gold">✶</span>{q}</li>
                ))}
              </ul>
              <div className="mt-3 flex justify-end">
                <Button
                  onClick={() => setAnswerReq(r)}
                  size="sm"
                  className="rounded-full bg-rust hover:bg-rust/90 text-cream px-5 h-9 text-xs"
                  data-testid={`answer-button-${r.id}`}
                >
                  {r.answered ? "Antwoord bewerken" : "Antwoord opstellen"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <AnswerDialog
        open={!!answerReq}
        onClose={() => setAnswerReq(null)}
        request={answerReq}
        token={token}
        onAnswered={load}
      />
    </div>
  );
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [mustChange, setMustChange] = useState(false);
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(null); setMustChange(false); };
  const handleLogin = (tk, must) => { setToken(tk); setMustChange(!!must); };
  return (
    <>
      {token ? <Dashboard token={token} onLogout={logout} mustChange={mustChange} /> : <LoginView onLogin={handleLogin} />}
      <Toaster position="top-center" theme="dark" />
    </>
  );
}
